const content = {
  component: 'PageTypeA',
  cssClass: 'page-two',
  meta: {
    title: 'CERN Scientists Boldly Go Into the Future with Cloud',
    description: `CERN is producing increasing amounts of data—and they need an effective solution to store it.`,
    og: {
      type: 'website',
      title: 'Cloud enables CERN to innovate—learn how',
      description: `The scientific organization is currently assessing cloud as a cost-effective, scalable way of storing the 
        results of their ground-breaking experiments.`,
      image: require('../../assets/images/social/og_page2.jpg')
    },
    twitter: {
      card: 'summary',
      title: 'Learn how the cloud enables CERN to push forward into the future.',
      description: `Scientific organisation CERN generates a huge amount of data from their experiments. So much, they’re assessing the 
        potential of cloud as a scalable solution to store their ground-breaking research.`,
      site: '@Oracle',
      creator: '@Oracle',
      image: require('../../assets/images/social/twitter_page2.jpg')
    }
  },
  title: 'CERN Scientists Boldly Go Into the Future with Cloud',
  heroImage: {
    preload: require('../../assets/images/backgrounds/page-two-pre.jpg'),
    loaded: require('../../assets/images/backgrounds/page-two.jpg')
  },
  sections: [
    { 
      component: 'SectionQuote', 
      content: {
        quote: `Essentially, Oracle Big Data Discovery can make life easier for the users by doing the dirty work for them`,
        author: `Manuel Martin Marquez`,
        position: `CERN Data Scientist`,
        type: 'quote'
      }
    },
    { 
      component: 'SectionCopy', 
      content: {
        copy: `
          For more than half a century, scientists at the CERN particle physics laboratory (more formally, the European Organisation for Nuclear Research) have been probing the inner workings of the universe. Today, the organisation, which was the birthplace of the World Wide Web, is assessing the potential of the cloud to make its research IT infrastructure more scalable and economical to operate.
        `,
        type: 'intro'
      }
    },
    { 
      component: 'SectionSeperator', 
      content: {
        image: null
      }
    },
    { 
      component: 'SectionCopy', 
      content: {
        copy: `
          <p>Since 2008, scientists from around the globe have been using the largest and most powerful particle accelerator in the world, CERN’s Large Hadron Collider to create conditions similar to the Big Bang, and from their studies, gain a better understanding of how our universe works.</p>
          <p>Just four years after the LHC’s inauguration, researchers discovered the Higgs boson, previously the last unverified piece of what is known as the Standard Model of particle physics. Nevertheless, the Standard Model explains only about 5% of how the universe actually works. Thus, as well as working to better define the characteristics of the Higgs boson, CERN and the worldwide particle physics research community are now hoping to make new discoveries that may shed some light on the rest. CERN’s researchers analyse huge numbers of subatomic particle collisions, which create many petabytes of raw data. Over the coming years, this analysis will require more computing power and storage capacity than CERN’s data centres—and budget—can currently handle.</p>
        `,
        type: 'default'
      }
    },
    { 
      component: 'SectionSeperator', 
      content: {
        image: require('../../assets/images/svg/database.svg')
      }
    },
    { 
      component: 'SectionCopy', 
      content: {
        copy: `
          <p>“Over the next 10 to 15 years, we expect the total amount of data produced by the experiments on the LHC to increase significantly,” says Alberto Di Meglio, head of CERN Openlab, a public-private partnership that accelerates the development of cutting-edge solutions for the worldwide LHC community and wider scientific research network.</p>
          <p>In addition to managing its own on-site data centres, Di Meglio believes CERN can efficiently pursue a hybrid model: procuring computing and storage services as needed via the cloud. “The cloud is becoming an increasingly important component of our research infrastructure,” says Di Meglio. However, managing, storing and analysing data in the cloud on the scale required by CERN can be very complex.</p>
        `,
        type: 'default'
      }
    },
    { 
      component: 'SectionQuote', 
      content: {
        quote: `Over the next 10 to 15 years, we expect the total amount of data produced by the experiments on the LHC to increase significantly.`,
        author: `Alberto Di Meglio`,
        position: `Head of CERN Openlab`,
        type: 'quote',
        backgroundImage: require('../../assets/images/backgrounds/page-two-sub-one.jpg')
      }
    },
    { 
      component: 'SectionCopy', 
      content: {
        copy: `
          <p>“These correlations are particularly important because we are conducting a variety of analyses for accelerator conditions and modes such as cooling down, warming up, and injecting beams of energy,” says Johannes Gutleber, senior engineer at CERN in charge of accelerator reliability and availability assessment.</p>
          <p>“Previously, we had a plethora of homegrown custom and third-party applications that partially stored custom files or dumped them into a relational database management system. While this was a great way to capture the data, getting it out to analyse it was a completely different story,”</p>
        `,
        type: 'default'
      }
    },
    { 
      component: 'SectionImage', 
      content: {
        image: require('../../assets/images/content/page-two-a.png'),
        alt: '',
        type: 'image'
      }
    },
    { 
      component: 'SectionCopy', 
      content: {
        copy: `
          <p>Based on testing carried out at the laboratory, Manuel Martin Marquez, a CERN data scientist, believes that the visual interface of Oracle Big Data Discovery has the potential to help the research scientists—most of whom aren’t data scientists—extract and interpret data from the Hadoop platform. They could then easily create analytics applications and share their findings, says Marquez.</p>
        `,
        type: 'default'
      }
    },
    // { 
    //   component: 'SectionQuote', 
    //   content: {
    //     quote: `Essentially, Oracle Big Data Discovery can make life easier for the users by doing the dirty work for them`,
    //     author: `Manuel Martin Marquez`,
    //     position: `CERN Data Scientist`,
    //     type: 'quote'
    //   }
    // },
    { 
      component: 'SectionCallToAction', 
      content: {
        title: `Transform your business with Oracle`,
        ctas: [
          { 
            image: require('../../assets/images/svg/brain.svg'),
            copy: 'Evolve your business with AI',
            url: 'https://www.oracle.com/uk/area-of-interest/it/tech-to-transform-your-business.html?source=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_AW_HI_CM_UK_C11_Q22_WP&intcmp=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_AW_HI_CM_UK_C11_Q22_WP',
            buttonCopy: 'Read report'
          },
          { 
            image: require('../../assets/images/svg/binoculars.svg'),
            copy: 'Explore Oracle with up to 3,500 free hours',
            url: 'https://cloud.oracle.com/datawarehouse?source=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_CT_TR_CM_UK_C18_Q24_DW&intcmp=:ow:o:cs:mt:RC_EMMK180813P00039:YTTFY19_IT_CT_TR_CM_UK_C18_Q24_DW',
            buttonCopy: 'Start your free trial'
          }
        ],
        type: 'cta'
      }
    }
  ]



}

export default content;